import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import { SimpleMenuButton } from "./Menubar";

function formatUndoStepAge(timestamp: number, now: number): string {
  const elapsedSeconds = Math.max(0, Math.floor((now - timestamp) / 1000));

  if (elapsedSeconds < 5) return state.translate("undoRedoButtons.now", "jetzt");
  if (elapsedSeconds < 45) {
    return state.translate("undoRedoButtons.aFewSecondsAgo", "vor einigen Sekunden");
  }

  const locale = state.settings.language || navigator.language || "de";
  const relativeTime = new Intl.RelativeTimeFormat(locale, { numeric: "always" });

  if (elapsedSeconds < 90) return relativeTime.format(-1, "minute");
  const elapsedMinutes = Math.round(elapsedSeconds / 60);
  if (elapsedMinutes < 60) return relativeTime.format(-elapsedMinutes, "minute");
  if (elapsedMinutes < 90) return relativeTime.format(-1, "hour");
  const elapsedHours = Math.round(elapsedMinutes / 60);
  if (elapsedHours < 24) return relativeTime.format(-elapsedHours, "hour");
  if (elapsedHours < 36) return relativeTime.format(-1, "day");
  const elapsedDays = Math.round(elapsedHours / 24);
  if (elapsedDays < 30) return relativeTime.format(-elapsedDays, "day");
  if (elapsedDays < 45) return relativeTime.format(-1, "month");
  const elapsedMonths = Math.round(elapsedDays / 30.4375);
  if (elapsedMonths < 12) return relativeTime.format(-elapsedMonths, "month");
  if (elapsedMonths < 18) return relativeTime.format(-1, "year");
  return relativeTime.format(-Math.round(elapsedMonths / 12), "year");
}

export function UndoButton() {
  useOnChangeWatcher(state.undoManager);

  const canUndo = state.undoManager.canUndo;
  const buttonHostRef = useRef<HTMLDivElement>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [flyoutPosition, setFlyoutPosition] = useState<{ right: number; top: number }>();
  const [relativeTimeNow, setRelativeTimeNow] = useState(() => Date.now());
  const undoSteps = state.undoManager.undoStepSummaries;

  const clearOpenTimer = () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    openTimerRef.current = undefined;
  };

  const clearCloseTimer = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = undefined;
  };

  const closeFlyout = () => {
    clearOpenTimer();
    clearCloseTimer();
    setFlyoutPosition(undefined);
  };

  const scheduleClose = () => {
    clearOpenTimer();
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setFlyoutPosition(undefined), 100);
  };

  const scheduleOpen = () => {
    clearCloseTimer();
    if (!canUndo || undoSteps.length === 0 || flyoutPosition) return;

    clearOpenTimer();
    openTimerRef.current = setTimeout(() => {
      const rect = buttonHostRef.current?.getBoundingClientRect();
      if (!rect) return;

      setFlyoutPosition({
        right: Math.max(8, window.innerWidth - rect.right),
        top: rect.bottom + 6,
      });
      setRelativeTimeNow(Date.now());
    }, 500);
  };

  useEffect(
    () => () => {
      clearOpenTimer();
      clearCloseTimer();
    },
    [],
  );

  useEffect(() => {
    if (!canUndo) closeFlyout();
  }, [canUndo]);

  useEffect(() => {
    if (!flyoutPosition) return;
    const timer = window.setInterval(() => setRelativeTimeNow(Date.now()), 10_000);
    return () => window.clearInterval(timer);
  }, [flyoutPosition]);

  const onUndo = () => {
    closeFlyout();
    state.undoManager.undo();
  };

  return (
    <div ref={buttonHostRef} onMouseEnter={scheduleOpen} onMouseLeave={scheduleClose}>
      <SimpleMenuButton
        onClick={onUndo}
        text={state.translate("undoRedoButtons.undo", "Undo")}
        disabled={!canUndo}
        iconName="undo" // no itx
      />
      {flyoutPosition &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleClose}
            style={{
              position: "fixed",
              right: flyoutPosition.right,
              top: flyoutPosition.top,
              zIndex: 10000,
              width: "max-content",
              maxWidth: `calc(100vw - ${flyoutPosition.right + 8}px)`,
              boxSizing: "border-box",
              maxHeight: `min(420px, calc(100vh - ${flyoutPosition.top + 8}px))`,
              overflowY: "auto",
              overflowX: "hidden",
              padding: "8px 0",
              color: "var(--knxColorBlack)",
              background: "white",
              border: "1px solid var(--knxColorLightGrey)",
              borderRadius: 4,
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25)",
            }}
          >
            {undoSteps.map((step) => (
              <div
                key={step.number}
                style={{
                  display: "grid",
                  gridTemplateColumns: "36px minmax(0, 1fr) auto",
                  gap: 8,
                  padding: "6px 12px 6px 0",
                }}
              >
                <span style={{ textAlign: "right", color: "grey" }}>{step.number}.</span>
                <span
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={step.name}
                >
                  {step.name}
                </span>
                <span
                  style={{
                    whiteSpace: "nowrap",
                    textAlign: "right",
                    alignSelf: "center",
                    color: "grey",
                    fontSize: "0.8em",
                  }}
                >
                  {formatUndoStepAge(step.timestamp, relativeTimeNow)}
                </span>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
export function RedoButton() {
  useOnChangeWatcher(state.undoManager);

  const canUndo = state.undoManager.canUndo;
  const canRedo = state.undoManager.canRedo;

  const onUndo = () => {
    state.undoManager.undo();
  };

  const onRedo = () => {
    state.undoManager.redo();
  };

  return (
    <SimpleMenuButton
      onClick={onRedo}
      text={state.translate("undoRedoButtons.redo", "Redo")}
      disabled={!canRedo}
      iconName="redo" // no itx
    />
  );
}

export function UndoDebugButton() {
  return (
    <SimpleMenuButton
      onClick={() => state.undoManager.logUndoSteps()}
      text="Undo-Debug"
      iconName="bug" // no itx
    />
  );
}
